import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  Modal,
  useTheme,
  useMediaQuery,
  TextareaAutosize,
} from "@mui/material";
import Confetti from "react-confetti"; // 🎉 Confetti effect
import Lottie from "react-lottie"; // 🎥 Animated success
import { colors, fonts, size } from "../../ColorFont/color";
import { url } from "../../ConstUrl/Url";
import { loadRazorpay } from "../../utils/loadRazorpay";
import { t } from "i18next";

const PaymentPopup = ({ plan, onClose, onPayNow, userData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openToast, setOpenToast] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handlePayNow = async () => {
    const res = await loadRazorpay();
    if (!res) {
      alert("Razorpay SDK failed to load. Check your internet connection.");
      return;
    }

    try {
      const orderResponse = await fetch(`${url}/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: plan.discountedPrice,
          currency: "INR",
        }),
      });

      const orderData = await orderResponse.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: `${userData.firstName} ${userData.lastName}`,
        description: `Payment for ${plan.name}`,
        order_id: orderData.id,
        email: userData.email,
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${url}/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(response),
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              setOpenToast(true);
              setShowSuccessModal(true); // 🎉 Show success modal with animation
              onPayNow(plan);
              // onClose();
              setTimeout(() => {
                setShowSuccessModal(false);
              }, 4000);
            } else {
              alert("Payment verification failed");
            }
          } catch (err) {
            alert("Error verifying payment. Check console logs.");
          }
        },
        prefill: {
          name: userData?.name || "Customer",
          email: userData?.email || "customer@example.com",
          contact: userData?.phone || "9999999999",
        },
        theme: { color: "#3399cc" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      alert("Something went wrong. Please try again later.");
    }
  };

  return (
    <>
      {!openToast && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 3000,
            backdropFilter: "blur(5px)",
            padding: isMobile ? "0 16px" : "0", // Adds padding on smaller screens
          }}
        >
          <Paper
            sx={{
              padding: isMobile ? 3 : 4,
              width: isMobile ? "90%" : "50%",
              maxWidth: 500,
              borderRadius: 12,
              background: "#fff",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
              border: "2px solid rgba(0, 255, 0, 0.4)",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.02)" },
            }}
          >
            {/* Plan Title */}
            <Typography
              sx={{
                fontSize: isMobile ? 24 : 30,
                fontWeight: 700,
                marginBottom: 3,
                fontFamily: fonts.poppins,
                color: colors.primary_dark,
              }}
            >
              {plan.name}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  fontFamily: fonts.poppins,
                }}
              >
                {plan.sponseredHrs && t("hours")}
                {plan.contacts && t("contacts")}
              </Typography>

              <Typography
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  fontFamily: fonts.poppins,
                  color: colors.primary_dark,
                  fontWeight: 600,
                }}
              >
                {plan.sponseredHrs && plan.sponseredHrs}
                {plan.contacts && plan.contacts}
              </Typography>
            </Box>

            {/* Price */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "flex-start",
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? 20 : 25,
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  marginTop: 1,
                  fontFamily: fonts.poppins,
                  color: colors.primary_dark,
                }}
              >
                ₹{plan.discountedPrice}
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    textDecoration: "line-through",
                    color: "gray",
                    fontSize: isMobile ? 14 : 16,
                  }}
                >
                  ₹{plan.originalPrice}
                </Typography>
              </Typography>
            </Box>

            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginTop: 4,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "100%",
                  backgroundImage: colors.button,
                  color: "#fff",
                  borderRadius: 2,
                  padding: isMobile ? "6px 16px" : "8px 20px",
                  fontSize: isMobile ? "14px" : size.formLabel,
                  fontFamily: fonts.poppins,
                }}
                onClick={handlePayNow}
              >
                {t("payNow")}
              </Button>

              <Button
                variant="outlined"
                sx={{
                  width: "100%",
                  borderColor: colors.primary_dark,
                  color: colors.primary_dark,
                  borderRadius: 2,
                  padding: isMobile ? "6px 16px" : "8px 20px",
                  fontSize: isMobile ? "14px" : size.formLabel,
                  fontFamily: fonts.poppins,
                }}
                onClick={onClose}
              >
                {t("cancel")}
              </Button>
            </Box>
          </Paper>

          {/* Snackbar (Toast Notification) */}
        </Box>
      )}
      {showSuccessModal && (
        <Snackbar
          open={openToast}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity="success"
            sx={{ backgroundColor: "black", color: "white" }}
          >
            Payment Successful! 🎉
          </Alert>
        </Snackbar>
      )}

      {/* Confetti Effect 🎉 */}
      {showSuccessModal && (
        <Confetti width={window.innerWidth} height={window.innerHeight} />
      )}
    </>
  );
};

export default PaymentPopup;
